import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "@/components/base/Select";
import ServicePricingStep from "@/pages/provider/setup/components/ServicePricingStep";
import { banks } from "@/mocks/settings";

const bankOptions = banks.map((b) => ({ value: b, label: b }));

const stepTitles = [
  "",
  "بيانات شركتك",
  "حدد خدماتك وأسعارك",
  "حدد منطقة التغطية",
  "بيانات الحساب البنكي",
];

const stepSubtitles = [
  "",
  "المعلومات اللي هتظهر للعملاء في التطبيق",
  "العميل هيشوف الأسعار دي في التطبيق",
  "العملاء داخل النطاق ده بس هم اللي يشوفوك",
  "عشان نحوّلك مستحقاتك — البيانات مشفرة وآمنة",
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);

  // Step 1
  const [companyName, setCompanyName] = useState("مغسلة اللمعة");
  const [description, setDescription] = useState(
    "متخصصون في غسيل وتلميع السيارات منذ 2018 — خدمة احترافية بمواد عالية الجودة",
  );
  const [city, setCity] = useState("الرياض");
  const [extraPhone, setExtraPhone] = useState("");

  // Step 3
  const [radius, setRadius] = useState(15);
  const [address, setAddress] = useState("حي النرجس، الرياض");

  // Step 4
  const [bank, setBank] = useState(banks[0]);
  const [iban, setIban] = useState("");
  const [accountName, setAccountName] = useState("");

  const inputClass =
    "h-12 w-full rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] px-4 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#0D6E6E]";
  const labelClass = "mb-2 block text-sm font-medium text-ghasla-text-primary";

  if (done) {
    return (
      <div dir="rtl" className="flex min-h-screen items-center justify-center bg-[#F0FDFD] p-6">
        <div className="w-full max-w-md rounded-3xl bg-white p-10 text-center shadow-card">
          <div className="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#E8F9F0]">
            <i className="ri-check-line text-4xl text-[#27AE60]" />
          </div>
          <h1 className="mt-6 text-[26px] font-bold text-ghasla-text-heading">حسابك جاهز! 🎉</h1>
          <p className="mt-2 text-base text-ghasla-text-primary">تقدر تبدأ تستقبل طلبات من الآن</p>
          <button
            type="button"
            onClick={() => navigate("/provider/dashboard")}
            className="mt-8 block w-full cursor-pointer rounded-xl bg-gradient-to-l from-[#00AAB7] to-[#1A9E96] px-6 py-3.5 text-base font-semibold text-white shadow-glow transition-opacity hover:opacity-90"
          >
            ادخل لوحة التحكم ←
          </button>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-[#F0FDFD]">
      {/* Top bar */}
      <header className="flex h-16 items-center justify-between bg-white px-8 shadow-topbar">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#00AAB7] to-[#33D1DA] text-white">
            <i className="ri-drop-fill text-lg" />
          </div>
          <span className="text-xl font-bold text-ghasla-text-heading">غسلة</span>
        </div>
        <span className="text-lg font-semibold text-ghasla-text-heading">إعداد حسابك</span>
        <span className="text-sm text-ghasla-text-secondary">الخطوة {step} من 4</span>
      </header>

      {/* Progress bar */}
      <div className="h-1 w-full bg-[#E8F0F0]">
        <div
          className="h-full bg-gradient-to-l from-[#00AAB7] to-[#33D1DA] transition-all duration-300"
          style={{ width: `${step * 25}%` }}
        />
      </div>

      {/* Content */}
      <div className="flex justify-center px-6 py-10">
        <div className="w-full max-w-[640px] rounded-3xl bg-white p-10 shadow-card">
          <h1 className="text-2xl font-bold text-ghasla-text-heading">{stepTitles[step]}</h1>
          <p className="mt-1 text-sm text-ghasla-text-primary">{stepSubtitles[step]}</p>

          <div className="mt-8 space-y-5">
            {/* STEP 1 */}
            {step === 1 && (
              <>
                <div>
                  <label className={labelClass}>اسم الشركة</label>
                  <input className={inputClass} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>الشعار</label>
                  <div className="flex items-center gap-4">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#F0FDFD] text-2xl font-bold text-ghasla-text-link">
                      {companyName.slice(0, 2)}
                    </div>
                    <button type="button" className="cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline">
                      ارفع شعار شركتك
                    </button>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>وصف مختصر</label>
                  <textarea
                    className="min-h-[80px] w-full rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] px-4 py-3 text-sm text-ghasla-text-heading outline-none focus:border-[#0D6E6E]"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>المدينة</label>
                  <input className={`${inputClass} cursor-not-allowed text-ghasla-text-secondary`} value={city} readOnly />
                </div>
                <div>
                  <label className={labelClass}>رقم تواصل إضافي (اختياري)</label>
                  <input className={inputClass} dir="ltr" value={extraPhone} onChange={(e) => setExtraPhone(e.target.value)} />
                </div>
              </>
            )}

            {/* STEP 2 */}
            {step === 2 && <ServicePricingStep />}

            {/* STEP 3 */}
            {step === 3 && (
              <>
                <div className="overflow-hidden rounded-2xl">
                  <iframe
                    title="خريطة التغطية"
                    src="https://www.google.com/maps?q=24.7136,46.6753&z=12&output=embed"
                    className="h-[280px] w-full border-0"
                    loading="lazy"
                  />
                </div>
                <div>
                  <label className={labelClass}>موقعك</label>
                  <input className={inputClass} value={address} onChange={(e) => setAddress(e.target.value)} />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm font-medium text-ghasla-text-primary">نطاق التغطية</label>
                    <span className="text-xl font-bold text-ghasla-text-link">{radius} كم</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={30}
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                    className="w-full accent-[#0D6E6E]"
                  />
                  <div className="mt-1 flex justify-between text-xs text-ghasla-text-secondary">
                    <span>1 كم</span>
                    <span>30 كم</span>
                  </div>
                </div>
              </>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <>
                <div>
                  <label className={labelClass}>اسم البنك</label>
                  <Select
                    value={bank}
                    onChange={setBank}
                    options={bankOptions}
                  />
                </div>
                <div>
                  <label className={labelClass}>رقم الآيبان IBAN</label>
                  <input className={inputClass} dir="ltr" placeholder="SA..." value={iban} onChange={(e) => setIban(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>اسم صاحب الحساب</label>
                  <input className={inputClass} value={accountName} onChange={(e) => setAccountName(e.target.value)} />
                  <p className="mt-2 text-xs text-ghasla-text-secondary">لازم يكون باسم الشركة أو المالك المسجّل</p>
                </div>
                <div className="flex items-center gap-2 rounded-xl bg-[#F0FDFD] px-4 py-3">
                  <i className="ri-lock-line text-ghasla-text-secondary" />
                  <span className="text-xs text-ghasla-text-secondary">بياناتك المالية مشفرة ومحمية</span>
                </div>
              </>
            )}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex items-center justify-between">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="cursor-pointer rounded-xl border border-[#E4EFEF] px-6 py-3 text-sm font-medium text-ghasla-text-primary hover:bg-[#F0FDFD]"
              >
                ← السابق
              </button>
            ) : (
              <span />
            )}
            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="cursor-pointer rounded-xl bg-gradient-to-l from-[#00AAB7] to-[#1A9E96] px-6 py-3 text-sm font-semibold text-white hover:opacity-90"
              >
                {step === 1 ? "التالي — الخدمات والأسعار" : step === 2 ? "التالي — النطاق الجغرافي" : "التالي — الحساب البنكي"} ←
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setDone(true)}
                className="cursor-pointer rounded-xl bg-gradient-to-l from-[#00AAB7] to-[#1A9E96] px-6 py-3 text-sm font-semibold text-white hover:opacity-90"
              >
                إنهاء الإعداد ✓
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}